import type { KeyboardEvent } from 'react';

type EmployerPendingReviewCardProps = {
  count: number;
  title: string;
  countUnitLabel: string;
  reviewNowLabel: string;
  onReviewNow: () => void;
};

export const EmployerPendingReviewCard = ({
  count,
  title,
  countUnitLabel,
  reviewNowLabel,
  onReviewNow,
}: EmployerPendingReviewCardProps) => {
  if (count <= 0) {
    return null;
  }

  const handleReviewKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onReviewNow();
    }
  };

  return (
    <article className="flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-amber-100/40 p-4 shadow-sm dark:border-amber-900/40 dark:from-amber-950/30 dark:to-amber-950/10">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.4}
              d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Zm-3 7v4m0 4h.01"
            />
          </svg>
        </div>
        <div className="min-w-0 flex flex-col gap-1">
          <span className="truncate text-xs font-semibold leading-snug text-amber-800/90 dark:text-amber-200/90">
            {title}
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-black tabular-nums leading-none text-amber-700 dark:text-amber-200">
              {count > 99 ? '99+' : count}
            </span>
            <span className="text-xs text-amber-700/80 dark:text-amber-300/80">{countUnitLabel}</span>
          </div>
        </div>
      </div>
      {/* Amber CTA kept on purpose: matches pending/warning card chrome (not a generic primary action). */}
      <button
        type="button"
        onClick={onReviewNow}
        onKeyDown={handleReviewKeyDown}
        className="min-h-11 shrink-0 rounded-xl bg-amber-500 px-4 py-2 text-xs font-bold text-white shadow-sm shadow-amber-500/20 transition-colors hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:bg-amber-600 dark:hover:bg-amber-500"
        aria-label={reviewNowLabel}
        tabIndex={0}
      >
        {reviewNowLabel}
      </button>
    </article>
  );
};
